import React from "react";
import _ from "lodash";
import Grid from "@material-ui/core/Grid";

import DashboardTable from "../../../../../../components/DashboardTable";
import { getBuySellTableStructure } from "./table-data";
import {TRANSACTION_TYPE_VALUES} from "../../../../constants";
import {splitEinzeltitelTradingInstruments} from '../../../../../RiskProfiling/utils';
import {USE_CUSTODIAN_ONLINE_PORTAL_INFO_TEXT} from "../../../TradeStep/components/PortfolioTrade/utils";
import EmptyTableMessage from "../../../../../RiskProfiling/components/EmptyTableMessage";
import InfoCard
  from "../../../../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/InfoCard";
import { useMPSummaryLine } from "../shared/mpSummaryLine";
import {DEFAULT_ADD_BTN} from "../shared/modelPortfolioTableColumns";
import {tableStructureForEinzeltitelBuyTrading, tableStructureForEinzeltitelSellTrading} from "./einzeltitelTable";
import {getValidityDateOptions} from "../../../../../RiskProfiling/einzeltitel_helper";


/**
 * Renders the buy/sell transactions table (funds + non-funds/einzeltitel).
 */
const TransactionsTable = ({
  transactionKey,
  transactions,
  portfolio,
  classes,
  isVirtual,
  isNewVirtual,
  isAnlageberatung,
  isWeightLoading,
  portfolioType,
  quotationFieldsSource,
  transactionsByProduct,
  minDepositDate,
  showTradingActions,
  // handlers
  handleTransactionValueChange,
  handleTransactionTypeChange,
  handleDiscountChange,
  handleNewVirtualTransactionWeightChanged,
  handleNewVirtualTransactionWeightTypeChanged,
  handleRemoveClick,
  handleSellAllChanged,
  handleSellAllQuantityChanged,
  handleOngoingFeeChange,
  handleEntryFeeChange,
  handleBookingDateChange,
  handleEinzeltitelSettingChange,
  handleLimitValueChange,
  handleQuantityChange,
  handleAddClick,
  showFunds,
  einzeltitelEnabled,
  clientSRI,
}) => {
  let nonFunds = [], funds = [];

  if(isVirtual){
    funds = transactions[transactionKey]; // for Virtual depots all assets should have "funds" like ordering process
  } else {
    [nonFunds, funds] = splitEinzeltitelTradingInstruments(transactions[transactionKey], portfolio.available_asset_classes, nonFunds, funds);
  }

  const structure = React.useMemo(
    () => getBuySellTableStructure()[portfolioType][transactionKey],
    [portfolioType, transactionKey]
  );

  const { dataSource: fundsDataSource, expandSummaryLineChange, checkExpanded } = useMPSummaryLine(portfolio, funds);

  const validityDateOptions = React.useMemo(() => getValidityDateOptions(), [])

  const emptyTableMsg = <EmptyTableMessage isMPorPI={portfolio.isModelportfolio} />

  const showFundsTable = showFunds || fundsDataSource.length > 0;
  const showEinzeltitel = (!!einzeltitelEnabled && !portfolio.isModelportfolio) || nonFunds.length > 0;

  return (
    <>
      {showFundsTable && (
        <>
          {showEinzeltitel && (
            <Grid item xs={12} className={classes.tableTitle}>Einzelfonds/Musterdepots</Grid>
          )}
          <Grid item xs={12} data-id={'funds-tables-container'} className={classes.tableContainer}>
            <DashboardTable
              tableLayout={"auto"}
              structure={structure}
              dataSource={fundsDataSource}
              expanded={true}
              tableClasses={classes}
              withFooter={true}
              withInputs={true}
              emptyDataMessage={emptyTableMsg}
              options={{
                isModelportfolio: portfolio.isModelportfolio,
                isVirtual: isVirtual,
                isNewVirtual,
                isAnlageberatung,
                hideAcceptanceCriteriasUnderName: isVirtual,
                action: transactionKey,
                itemsLength: funds.length,
                minDepositDate: minDepositDate,
                discountsDisabled: !_.get(portfolio, 'ordering_discounts_allowed', []).includes('trading'),
                availableDiscounts: portfolio.availableDiscounts,
                combinedDiscounts: portfolio.combinedDiscounts,
                depotDiscount: portfolio.discount,
                maxQtyDecimals: portfolio.maxQtyDecimals,
                transactionsWeightType: portfolio.transactionsWeightType,
                isWeightLoading,
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.BUY]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.BUY]],
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.SELL]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.SELL]],
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.SWITCH]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.SWITCH]],
                handleTransactionValueChange: handleTransactionValueChange,
                handleTransactionTypeChange: handleTransactionTypeChange,
                handleDiscountChange: handleDiscountChange,
                handleTransactionWeightChanged: handleNewVirtualTransactionWeightChanged,
                handleTransactionWeightTypeChanged: handleNewVirtualTransactionWeightTypeChanged,
                handleRemoveClick: showTradingActions && handleRemoveClick,
                handleSellAllChanged: handleSellAllChanged,
                handleOngoingFeeChange: handleOngoingFeeChange,
                handleEntryFeeChange: handleEntryFeeChange,
                clientSRRI: clientSRI,
                existingSwitchOuts: transactionsByProduct[TRANSACTION_TYPE_VALUES.SWITCH] || {},
                handleBookingDateChange: handleBookingDateChange,
                hasMultipleOrders: funds.length > 1,
                addButtonText: portfolio.isModelportfolio ? DEFAULT_ADD_BTN : undefined,
                handleAddClick: handleAddClick,
                expandSummaryLineChange: expandSummaryLineChange,
                checkExpanded: checkExpanded,
              }}
            />
          </Grid>
        </>
      )}
      {showEinzeltitel && (
        <>
          <Grid item xs={12} className={classes.tableTitle}>Einzeltitel/Aktien</Grid>
          <Grid item xs={12}>
            <InfoCard uid={"einzeltitel_info_text"} info_text={USE_CUSTODIAN_ONLINE_PORTAL_INFO_TEXT} isNewDesign />
          </Grid>
          <Grid item xs={12} data-id={'einzeltitel-tables-container'} className={classes.tableContainer}>
            <DashboardTable
              tableLayout={"auto"}
              structure={transactionKey === TRANSACTION_TYPE_VALUES.BUY ? tableStructureForEinzeltitelBuyTrading : tableStructureForEinzeltitelSellTrading}
              dataSource={nonFunds}
              expanded={true}
              tableClasses={classes}
              withFooter={true}
              withInputs={true}
              emptyDataMessage={emptyTableMsg}
              options={{
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.BUY]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.BUY]],
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.SELL]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.SELL]],
                [quotationFieldsSource[TRANSACTION_TYPE_VALUES.SWITCH]]: portfolio[quotationFieldsSource[TRANSACTION_TYPE_VALUES.SWITCH]],
                action: transactionKey,
                itemsLength: nonFunds.length,
                minDepositDate: minDepositDate,
                validityDateOptions: validityDateOptions,
                stockExchangeOptions: portfolio.stock_exchange_options,
                orderTypeOptions: transactionKey === TRANSACTION_TYPE_VALUES.BUY ? portfolio.einzeltitel_order_type_buy : portfolio.einzeltitel_order_type_sell,
                maxQtyDecimals: portfolio.maxQtyDecimals,
                onChange: handleEinzeltitelSettingChange,
                handleLimitValueChange: handleLimitValueChange,
                handleQuantityChange: handleQuantityChange,
                handleRemoveClick: showTradingActions && handleRemoveClick,
                handleSellAllChanged: handleSellAllQuantityChanged,
                clientSRRI: clientSRI,
                handleAddClick: handleAddClick,
                hasMultipleShares: nonFunds.length > 1
              }}
            />
          </Grid>
        </>
      )}
    </>
  )
};

export default TransactionsTable;
